import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cryptoAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import TradingViewChart from '../components/TradingViewChart';
import './Markets.css';

const cryptoNames = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  BNB: 'BNB',
  XRP: 'XRP',
  ADA: 'Cardano',
  SOL: 'Solana',
  DOGE: 'Dogecoin',
  DOT: 'Polkadot',
  POL: 'Polygon',
  LTC: 'Litecoin',
  AVAX: 'Avalanche',
  LINK: 'Chainlink',
  TRX: 'TRON',
  SHIB: 'Shiba Inu',
  UNI: 'Uniswap',
  ATOM: 'Cosmos',
  XLM: 'Stellar',
  NEAR: 'NEAR Protocol',
  BCH: 'Bitcoin Cash',
  TON: 'Toncoin'
};

const Markets = () => {
  const { t, language } = useLanguage();
  const [prices, setPrices] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [chartCrypto, setChartCrypto] = useState(null); // crypto affichée en plein écran (TradingView)
  const [showChartTools, setShowChartTools] = useState(false); // colonne d'outils de dessin du graphique

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await cryptoAPI.getPrices(currency.toLowerCase());
        setPrices(response.data.prices);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [currency]);

  // ─────────────────────────────────────────────────────────────
  // FLUX TEMPS RÉEL — WebSocket public Binance, ouvert DEPUIS LE NAVIGATEUR.
  // Aucune charge ni risque côté serveur. Si Binance est injoignable, le
  // polling HTTP 30s ci-dessus reste le repli. Les prix Binance sont en USDT
  // (~USD) : on convertit vers la devise sélectionnée avec les mêmes taux que
  // le serveur (priceCollector). change24h est un %, volume est déjà en USD.
  // ─────────────────────────────────────────────────────────────
  const pendingTicksRef = useRef({});
  useEffect(() => {
    // Paires confirmées sur Binance (USDT). Les autres (ex: TON) restent en HTTP.
    const BINANCE_PAIRS = {
      btcusdt: 'BTC', ethusdt: 'ETH', bnbusdt: 'BNB', xrpusdt: 'XRP',
      adausdt: 'ADA', solusdt: 'SOL', dogeusdt: 'DOGE', dotusdt: 'DOT',
      polusdt: 'POL', ltcusdt: 'LTC', avaxusdt: 'AVAX', linkusdt: 'LINK',
      trxusdt: 'TRX', shibusdt: 'SHIB', uniusdt: 'UNI', atomusdt: 'ATOM',
      xlmusdt: 'XLM', nearusdt: 'NEAR', bchusdt: 'BCH'
    };
    const USD_TO = { USD: 1, EUR: 0.92, XOF: 600, GNF: 8600 };
    const rate = USD_TO[currency] ?? 1;

    const streams = Object.keys(BINANCE_PAIRS).map(p => `${p}@ticker`).join('/');
    let ws;
    try {
      ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
    } catch {
      return; // navigateur/env sans WebSocket → on garde le repli HTTP
    }

    ws.onmessage = (evt) => {
      try {
        const { data } = JSON.parse(evt.data);
        if (!data || !data.s) return;
        const symbol = BINANCE_PAIRS[data.s.toLowerCase()];
        if (!symbol) return;
        // Accumule; on applique par lots (voir setInterval) pour rester fluide
        pendingTicksRef.current[symbol] = {
          price: parseFloat(data.c) * rate,
          change24h: parseFloat(data.P),
          volume24h: parseFloat(data.q) // quote volume (USDT ≈ USD)
        };
      } catch { /* ignore ticks malformés */ }
    };

    // Applique les ticks accumulés au plus une fois par seconde (near-real-time,
    // sans re-render à chaque message ~20/s).
    const flush = setInterval(() => {
      const pending = pendingTicksRef.current;
      if (Object.keys(pending).length === 0) return;
      pendingTicksRef.current = {};
      setPrices(prev => prev.map(p =>
        pending[p.symbol] ? { ...p, ...pending[p.symbol] } : p
      ));
    }, 1000);

    return () => {
      clearInterval(flush);
      pendingTicksRef.current = {};
      try { ws.close(); } catch { /* déjà fermé */ }
    };
  }, [currency]);

  // Bloquer le scroll de la page derrière le modal graphique.
  // Technique position:fixed — la seule fiable sur iOS Safari
  // (overflow:hidden seul ne bloque pas le scroll tactile sur iPhone).
  useEffect(() => {
    if (chartCrypto) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [chartCrypto]);

  // Fermer avec la touche Échap (desktop)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setChartCrypto(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const formatPrice = (price) => {
    if (currency === 'XOF' || currency === 'GNF') {
      return new Intl.NumberFormat('fr-FR').format(Math.round(price));
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: price < 1 ? 6 : 2
    }).format(price);
  };

  const currencySymbols = { USD: '$', EUR: '€', XOF: 'CFA ', GNF: 'GNF ' };

  // Prix live de la crypto affichée dans le modal : suit les mises à jour
  // (HTTP 30s + flux Binance temps réel ci-dessous) au lieu de rester figé
  // sur la photo prise au moment du clic.
  const liveChartCrypto = chartCrypto
    ? (prices.find(p => p.symbol === chartCrypto.symbol) || chartCrypto)
    : null;

  return (
    <Layout>
      <div className="markets-page">
        {/* Header */}
        <div className="markets-header">
          <h1>{t('markets.title')}</h1>
          <div className="currency-toggle">
            {['USD', 'EUR', 'XOF', 'GNF'].map((curr) => (
              <button
                key={curr}
                className={`currency-btn ${currency === curr ? 'active' : ''}`}
                onClick={() => setCurrency(curr)}
              >
                {curr}
              </button>
            ))}
          </div>
        </div>

        {/* Market List */}
        <div className="markets-list">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="market-card skeleton">
                <div className="skeleton-circle"></div>
                <div className="skeleton-lines">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
              </div>
            ))
          ) : (
            prices.map((crypto) => (
              <div
                key={crypto.symbol}
                className="market-card"
                onClick={() => { setChartCrypto(crypto); setShowChartTools(false); }}
              >
                <div className="market-card-main">
                  <div className="market-card-left">
                    <div className={`market-icon ${crypto.symbol.toLowerCase()}`}>
                      {crypto.symbol.charAt(0)}
                    </div>
                    <div className="market-info">
                      <span className="market-symbol">{crypto.symbol}</span>
                      <span className="market-name">{cryptoNames[crypto.symbol] || crypto.symbol}</span>
                    </div>
                  </div>

                  <div className="market-card-center">
                    <svg viewBox="0 0 100 30" className="mini-chart">
                      <polyline
                        fill="none"
                        stroke={crypto.change24h >= 0 ? '#0ecb81' : '#f6465d'}
                        strokeWidth="2"
                        points={crypto.change24h >= 0
                          ? "0,25 15,22 30,18 45,20 60,12 75,8 100,5"
                          : "0,5 15,8 30,12 45,15 60,18 75,22 100,25"
                        }
                      />
                    </svg>
                  </div>

                  <div className="market-card-right">
                    <span className="market-price">
                      {currencySymbols[currency]}{formatPrice(crypto.price)}
                    </span>
                    <span className={`market-change ${crypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                      {crypto.change24h >= 0 ? '▲' : '▼'} {Math.abs(crypto.change24h).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modal plein écran: graphique TradingView (lecture seule).
            Rendu via PORTAL directement dans document.body — la méthode
            standard des librairies de modals: aucun parent (transform,
            overflow, stacking context) ne peut le piéger ou le recouvrir. */}
        {chartCrypto && createPortal(
          <div className="chart-modal-overlay">
            {/* Bouton de fermeture flottant: TOUJOURS visible */}
            <button
              className="chart-modal-close-floating"
              onClick={() => setChartCrypto(null)}
              aria-label="Fermer"
            >
              ×
            </button>
            <div className="chart-modal">
              <div className="chart-modal-header">
                <div className="chart-modal-title">
                  <div className={`market-icon ${chartCrypto.symbol.toLowerCase()}`}>
                    {chartCrypto.symbol.charAt(0)}
                  </div>
                  <div className="chart-modal-info">
                    <span className="chart-modal-symbol">
                      {chartCrypto.symbol}
                      <span className="chart-modal-name">{cryptoNames[chartCrypto.symbol] || chartCrypto.symbol}</span>
                    </span>
                    <span className="chart-modal-price">
                      {currencySymbols[currency]}{formatPrice(liveChartCrypto.price)}
                      <span className={`market-change ${liveChartCrypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                        {liveChartCrypto.change24h >= 0 ? '▲' : '▼'} {Math.abs(liveChartCrypto.change24h).toFixed(2)}%
                      </span>
                    </span>
                    <span className="chart-modal-meta">
                      {t('markets.volume')}: ${(liveChartCrypto.volume24h / 1e9).toFixed(2)}B · {t('markets.marketCap')}: ${(liveChartCrypto.marketCap / 1e9).toFixed(2)}B
                    </span>
                  </div>
                </div>
                <div className="chart-modal-actions">
                  <button
                    className={`chart-tools-toggle ${showChartTools ? 'active' : ''}`}
                    onClick={() => setShowChartTools(v => !v)}
                    aria-pressed={showChartTools}
                    title={t('markets.toggleTools')}
                  >
                    🛠 {t('markets.tools')}
                  </button>
                  <button className="chart-modal-close" onClick={() => setChartCrypto(null)}>×</button>
                </div>
              </div>
              <div className="chart-modal-body">
                <TradingViewChart symbol={chartCrypto.symbol} locale={language} showTools={showChartTools} />
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </Layout>
  );
};

export default Markets;
